/* Super Admin > Schools > Duplicate Review.
   Side-by-side review of queued duplicate groups with Merge / Keep Separate and
   choose-master actions. Merges preserve assignments/portfolios/workspaces.
   Mounted at /app/school-duplicates. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { withTimeout } from "@/hooks/useLoadState";
import { toast } from "sonner";
import { Loader2, GitMerge, ShieldCheck, Building2, MapPin, Hash, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const apiErr = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d[0]?.msg || fallback;
  return d?.msg || fallback;
};

const confColor = (c) =>
  c >= 98 ? "bg-emerald-100 text-emerald-800"
    : c >= 95 ? "bg-blue-100 text-blue-800"
      : c >= 90 ? "bg-amber-100 text-amber-800"
        : "bg-slate-100 text-slate-700";

const Cell = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-1.5 text-[12px]">
    <Icon className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
    <span className="text-slate-400 w-20 shrink-0">{label}</span>
    <span className="text-slate-800 break-words">{value || <span className="text-slate-300">—</span>}</span>
  </div>
);

const GroupCard = ({ group, onMerge, onKeep, busyId }) => {
  const [masterId, setMasterId] = useState(group.master_id);
  const busy = busyId === group.id;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`dup-group-${group.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-[11.5px] font-bold px-2 py-0.5 rounded-full ${confColor(group.confidence)}`}>{group.confidence}% · {group.status}</span>
          <span className="text-[12px] text-slate-500">{group.label}</span>
        </div>
        <span className="text-[12px] text-slate-400">{group.members?.length || 0} records</span>
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(group.members?.length || 1, 3)}, minmax(0,1fr))` }}>
        {(group.members || []).map((m) => {
          const isMaster = m.id === masterId;
          return (
            <div key={m.id} className={`rounded-lg border p-3 ${isMaster ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`dup-member-${m.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-[13px] text-slate-900 leading-tight">{m.school_name}</div>
                {isMaster && <span className="flex items-center gap-1 text-[11.5px] font-bold text-emerald-700"><Crown className="h-3 w-3" />MASTER</span>}
              </div>
              <div className="space-y-1">
                <Cell icon={Hash} label="NCES" value={m.nces_school_id} />
                <Cell icon={Hash} label="State ID" value={m.state_school_id} />
                <Cell icon={MapPin} label="Address" value={m.street_address} />
                <Cell icon={Building2} label="City/ST" value={`${m.city || ""} ${m.state || ""} ${m.zip || ""}`.trim()} />
                <Cell icon={ShieldCheck} label="Portfolio" value={m.assigned_portfolio_id} />
                <Cell icon={Building2} label="Workspaces" value={m.dependency_workspaces} />
                <Cell icon={Hash} label="Source" value={`${m.import_source || ""} ${m.source_year || ""}`.trim()} />
              </div>
              {!isMaster && (
                <button onClick={() => setMasterId(m.id)} data-testid={`dup-choose-master-${m.id}`}
                  className="mt-2 text-[11.5px] font-semibold text-blue-700 hover:underline">Make this the master</button>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => onMerge(group, masterId)} data-testid={`dup-merge-${group.id}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <GitMerge className="h-4 w-4 mr-1" />}Merge into master
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onKeep(group)} data-testid={`dup-keep-${group.id}`}>Keep Separate</Button>
      </div>
    </div>
  );
};

const DuplicateReviewPage = ({ session }) => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const headers = useMemo(() => ({
    "x-user-id": session?.user?.id || "admin",
    "x-user-role": session?.user?.role || "admin",
    "x-tenant-id": session?.tenant?.id || "tenant_1",
  }), [session]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const r = await withTimeout(axios.get(`${API}/api/admin/schools/duplicates/groups`, { headers, params: { page, status, page_size: 20 } }));
      setData(r.data);
    } catch (e) { setLoadErr(apiErr(e, "Could not load groups")); }
    setLoading(false);
  }, [headers, page, status]);
  useEffect(() => { load(); }, [load]);

  const onMerge = async (group, masterId) => {
    setBusyId(group.id);
    try {
      const r = await axios.post(`${API}/api/admin/schools/duplicates/merge`,
        { group_id: group.id, master_id: masterId }, { headers });
      toast.success(`Merged ${r.data.merged} record(s) into master`);
      load();
    } catch (e) { toast.error(apiErr(e, "Merge failed")); }
    setBusyId(null);
  };
  const onKeep = async (group) => {
    setBusyId(group.id);
    try {
      await axios.post(`${API}/api/admin/schools/duplicates/keep-separate`, { group_id: group.id }, { headers });
      toast.success("Marked as separate schools");
      load();
    } catch (e) { toast.error(apiErr(e, "Action failed")); }
    setBusyId(null);
  };

  return (
    <div className="max-w-[1200px] mx-auto" data-testid="duplicate-review-page">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Duplicate Review</h1>
          <p className="text-sm text-slate-500 mt-1">Confirm or dismiss possible duplicate schools. Merges preserve assignments, portfolios, licenses and diagnostics.</p>
        </div>
        <div className="flex items-center gap-2">
          {["", "Confirmed Duplicate", "Possible Duplicate"].map((s) => (
            <button key={s || "all"} onClick={() => { setStatus(s); setPage(1); }} data-testid={`dup-filter-${s || "all"}`}
              className={`text-[12px] font-semibold px-3 py-1.5 rounded-md ${status === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      {loadErr && !loading ? (
        <div role="alert" className="rounded-xl border border-[#fecaca] bg-[#fef2f2] p-8 text-center" data-testid="dup-error">
          <div className="text-[15px] font-semibold text-[#991b1b]">Failed to load duplicate groups</div>
          <div className="text-[13px] text-[#4b5563] mt-1">{loadErr}</div>
          <button onClick={load} className="mt-3 h-9 px-4 rounded-lg border border-slate-200 bg-white text-[13px] font-semibold text-slate-900 hover:bg-slate-50" data-testid="dup-retry">Retry</button>
        </div>
      ) : loading ? (
        <div className="text-slate-500 text-sm py-10 text-center" aria-busy="true"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading duplicate groups…</div>
      ) : !data?.groups?.length ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="dup-empty">
          <ShieldCheck className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
          <p className="text-slate-700 font-medium">No duplicate groups awaiting review.</p>
          <p className="text-slate-400 text-sm mt-1">Run a deduplication from the Schools import page to populate this queue.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="dup-group-list">
          {data.groups.map((g) => (
            <GroupCard key={g.id} group={g} onMerge={onMerge} onKeep={onKeep} busyId={busyId} />
          ))}
          <div className="flex items-center justify-between pt-2">
            <span className="text-[12px] text-slate-500">{data.total} groups · page {data.page} / {data.pages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="dup-prev">Prev</Button>
              <Button size="sm" variant="outline" disabled={page >= (data.pages || 1)} onClick={() => setPage((p) => p + 1)} data-testid="dup-next">Next</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DuplicateReviewPage;
